/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent, ReactNode } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { Separator } from 'ui/components/Separator'
import { Button } from 'ui/designSystem/Button/Button'
import { ModalDetailedFooterProps } from 'ui/designSystem/ModalDetailed/types'
import { Typo } from 'ui/theme'

export const ModalDetailedFooter: FunctionComponent<ModalDetailedFooterProps> = ({
  buttonsDescription,
  primaryButton,
  secondaryButton,
  tertiaryButton,
}) => {
  const { designSystem, isDesktopViewport, isTabletViewport } = useTheme()
  const isLargeViewport = !!(isDesktopViewport || isTabletViewport)

  const buttons: ReactNode[] = [
    primaryButton ? (
      <Button
        key="primary"
        {...primaryButton}
        variant="primary"
        color="brand"
        fullWidth={!isLargeViewport}
      />
    ) : null,
    secondaryButton ? (
      <Button
        key="secondary"
        {...secondaryButton}
        variant="secondary"
        color="neutral"
        fullWidth={!isLargeViewport}
      />
    ) : null,
    tertiaryButton ? (
      <Button
        key="tertiary"
        {...tertiaryButton}
        variant="tertiary"
        color="neutral"
        fullWidth={!isLargeViewport}
      />
    ) : null,
  ].filter(Boolean)

  const orderedButtons = isLargeViewport ? [...buttons].reverse() : buttons
  const description = buttonsDescription ? (
    <ButtonsDescription isLargeViewport={isLargeViewport}>{buttonsDescription}</ButtonsDescription>
  ) : null

  return (
    <FooterContainer>
      {isDesktopViewport ? <Separator.Horizontal color={designSystem.color.border.subtle} /> : null}
      <ActionsContainer isLargeViewport={isLargeViewport}>
        {isLargeViewport ? null : description}
        <ButtonsContainer isLargeViewport={isLargeViewport}>{orderedButtons}</ButtonsContainer>
        {isLargeViewport ? description : null}
      </ActionsContainer>
    </FooterContainer>
  )
}

const FooterContainer = styled.View({
  flexShrink: 0,
  width: '100%',
})

const ActionsContainer = styled.View<{ isLargeViewport: boolean }>(
  ({ theme, isLargeViewport }) => ({
    flexDirection: isLargeViewport ? 'row' : 'column',
    alignItems: isLargeViewport ? 'center' : 'stretch',
    rowGap: theme.designSystem.size.spacing.m,
    columnGap: theme.designSystem.size.spacing.m,
    paddingTop: theme.designSystem.size.spacing.xl,
  })
)

const ButtonsContainer = styled.View<{ isLargeViewport: boolean }>(
  ({ theme, isLargeViewport }) => ({
    flexDirection: isLargeViewport ? 'row' : 'column',
    alignItems: isLargeViewport ? 'center' : 'stretch',
    flexWrap: 'nowrap',
    flexShrink: isLargeViewport ? 0 : 1,
    rowGap: theme.designSystem.size.spacing.l,
    columnGap: theme.designSystem.size.spacing.l,
  })
)

const ButtonsDescription = styled(Typo.BodyXs)<{ isLargeViewport: boolean }>(
  ({ theme, isLargeViewport }) => ({
    color: theme.designSystem.color.text.subtle,
    flexShrink: 1,
    textAlign: isLargeViewport ? 'left' : 'center',
  })
)
